'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher/tracked-anchor';
import { TEN_YEARS_POSITION } from '../_const/gtag';
import { LogoLockup } from './logo-lockup';

// 섹션 래퍼 id 는 layout.tsx 와 같아야 한다
const GNB_MENU = [
  { id: 'ceo-letter', key: 'ceoLetter' },
  { id: '10-story', key: 'stories' },
  { id: 'our-10-years', key: 'stats' },
  { id: 'challenge', key: 'challenge' },
] as const;

// 이보다 작은 스크롤 이동은 손떨림으로 보고 GNB 를 숨기거나 되살리지 않는다
const SCROLL_THRESHOLD_PX = 8;

export function Gnb() {
  const t = useTranslations('TenYears.gnb');
  const [hidden, setHidden] = useState(false);

  useEffect(function hideOnScrollDown() {
    let lastY = window.scrollY;

    function onScroll() {
      const y = window.scrollY;
      if (y <= 0) {
        setHidden(false);
        lastY = y;
        return;
      }
      // lastY 를 갱신하지 않아 느린 스크롤도 누적돼 임계값을 넘는다
      if (Math.abs(y - lastY) < SCROLL_THRESHOLD_PX) return;
      setHidden(y > lastY);
      lastY = y;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    return function unsubscribe() {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 h-14 bg-white px-5 motion-safe:transition-transform motion-safe:duration-300 md:h-[100px] xl:px-20',
        hidden && '-translate-y-full',
        // 숨은 상태에서 키보드 포커스가 들어오면 다시 보인다. focus-within 은 마우스 클릭 포커스에도 걸려
        // nav 로 섹션 이동한 뒤 GNB 가 숨지 않으므로 focus-visible 로 한정한다
        'has-[:focus-visible]:translate-y-0',
      )}
    >
      <nav
        aria-label={t('ariaLabel')}
        className="mx-auto flex h-full max-w-[1200px] items-center gap-[30px]"
      >
        <HomeLink />
        <NavMenu>
          {GNB_MENU.map(({ id, key }) => (
            <NavLink key={id} sectionId={id}>
              {t(key)}
            </NavLink>
          ))}
        </NavMenu>
      </nav>
    </header>
  );
}

function HomeLink() {
  return (
    <TrackedAnchor
      href="/"
      label="10years-go-to-home"
      position={TEN_YEARS_POSITION.GNB}
      className="shrink-0 rounded-[13.566px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-3b-blue-light-600"
    >
      <LogoLockup tone="navy" priority />
    </TrackedAnchor>
  );
}

// 모바일은 로고만 보인다. 버튼 그룹은 오른쪽 정렬, 버튼 사이 20px(디자인 QA 2026-09-29)
function NavMenu({ children }: { children: ReactNode }) {
  return (
    <ul className="ml-auto hidden min-w-0 items-center gap-5 md:flex">
      {children}
    </ul>
  );
}

function NavLink({
  sectionId,
  children,
}: {
  sectionId: string;
  children: ReactNode;
}) {
  return (
    <li className="min-w-0 shrink">
      <TrackedAnchor
        href={`#${sectionId}`}
        onClick={function offsetAnchorByDirection() {
          // 아래로 가면 GNB 가 숨으므로 앵커 여백 0, 위로 가면 GNB 가 남으므로 기본값(GNB 높이, layout ANCHOR_CLASS)
          const top =
            document.getElementById(sectionId)?.getBoundingClientRect().top ??
            0;
          const root = document.documentElement.style;
          if (top > SCROLL_THRESHOLD_PX)
            root.setProperty('--ty-anchor-offset', '0px');
          else root.removeProperty('--ty-anchor-offset');
        }}
        label={`10years-scroll-to-${sectionId}`}
        position={TEN_YEARS_POSITION.GNB}
        className="flex h-[52px] items-center justify-center whitespace-nowrap rounded-[8px] px-4 text-[16px] font-semibold leading-6 tracking-[-0.18px] text-3b-blue-light-900 transition-colors hover:bg-3b-background-medium focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-3b-blue-light-600"
      >
        {children}
      </TrackedAnchor>
    </li>
  );
}
